import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { CandidateActions, CandidateStatusText, OutcomeBadge } from '../components/imports';
import { Card, btnGhost } from '../components/ui';
import { ApiError, type CandidateDetail as Detail } from '../lib/api';
import { categoryLabel, formatRange } from '../lib/format';
import { useSession } from '../lib/session';

const times = (dates: Array<{ date: string; start?: string | undefined }>) =>
  [...new Set(dates.map((d) => d.start).filter(Boolean))].join(', ');

/**
 * Un candidato a confronto con gli eventi simili: cosa ha letto la pipeline, i testi riscritti
 * (quelli che si pubblicano) e la decisione. Il testo originale della sorgente è solo per chi
 * rivede: sul sito vanno sempre i testi riscritti.
 */
export function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, canEdit, config } = useSession();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await api<Detail>(`/imports/candidates/${id}`));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };
  useEffect(() => {
    if (canEdit) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canEdit]);

  if (error)
    return (
      <main className="mx-auto max-w-[1400px] p-4 md:p-6">
        <p role="alert" className="bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      </main>
    );
  if (!data) return <main className="p-6 text-sm text-muted">Caricamento…</main>;
  const { candidate: c, row, events } = data;
  const n = c.normalized;
  const en = c.enriched;

  return (
    <main className="mx-auto max-w-[1400px] p-4 md:p-6">
      <button onClick={() => navigate(-1)} className={btnGhost}>
        <ArrowLeft className="size-4" aria-hidden /> Indietro
      </button>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-h2">{row.title}</h1>
        <OutcomeBadge outcome={row.outcome} />
        <CandidateStatusText row={row} />
        <div className="ml-auto">
          <CandidateActions row={row} api={api} onDone={() => void load()} />
        </div>
      </div>
      <p className="mt-1 text-sm text-muted">
        {c.sourceId}
        {row.sourceUrl && (
          <>
            {' ('}
            <a href={row.sourceUrl} target="_blank" rel="noopener" className="hover:underline">
              pagina della sorgente ↗
            </a>
            {')'}
          </>
        )}{' '}
        · {formatRange(row.startDate, row.endDate)} · {row.comune} · confidenza{' '}
        {c.confidence.toFixed(2).replace('.', ',')}
        {c.runId && (
          <>
            {' · '}
            <Link to={`/sources/runs/${c.runId}`} className="hover:underline">
              esecuzione
            </Link>
          </>
        )}
      </p>
      {row.review?.eventId && (
        <p className="mt-2 text-sm">
          <Link to={`/events/${row.review.eventId}`} className="font-medium hover:underline">
            Apri l’evento
          </Link>
        </p>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Letto dalla sorgente">
          <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted">Titolo</dt>
            <dd>{n?.title ?? c.extracted.title}</dd>
            <dt className="text-muted">Luogo</dt>
            <dd>
              {c.extracted.placeText}
              {!n?.comuneId && <span className="ml-2 text-danger">comune non riconosciuto</span>}
            </dd>
            <dt className="text-muted">Date</dt>
            <dd>
              {formatRange(row.startDate, row.endDate)}
              {n && times(n.dates) && ` · ore ${times(n.dates)}`}
              {n && n.dates.length > 2 && ` · ${n.dates.length} giorni`}
            </dd>
            <dt className="text-muted">Categoria</dt>
            <dd>
              {categoryLabel(row.category)}
              {!c.extracted.category && en?.category && ' (scelta dall’AI)'}
              {!c.extracted.category && !en?.category && ' (predefinita)'}
            </dd>
            {c.extracted.priceText && (
              <>
                <dt className="text-muted">Prezzo</dt>
                <dd>{c.extracted.priceText}</dd>
              </>
            )}
          </dl>
          {c.issues.length > 0 && (
            <ul className="text-sm">
              {c.issues.map((i) => (
                <li key={i.code} className={i.severity === 'error' ? 'text-danger' : 'text-muted'}>
                  {i.severity === 'error' ? '✕' : i.severity === 'warning' ? '⚠' : 'ℹ'} {i.message}
                </li>
              ))}
            </ul>
          )}
          {c.extracted.description && (
            <details className="text-sm">
              <summary className="cursor-pointer text-muted">
                Testo originale (non si pubblica)
              </summary>
              <p className="mt-2 whitespace-pre-line text-muted">{c.extracted.description}</p>
            </details>
          )}
        </Card>

        <Card title="Testi da pubblicare">
          {en ? (
            <div className="flex flex-col gap-3 text-sm">
              <p className="whitespace-pre-line">{en.description}</p>
              <p>
                <span className="text-muted">In breve: </span>
                {en.shortDesc}
              </p>
              <p className="text-xs text-muted">
                SEO: {en.seoTitle} — {en.seoDescription}
              </p>
              <p className="text-xs text-muted">
                {en.model} · {en.promptVersion}
              </p>
            </div>
          ) : row.outcome === 'new' ? (
            <p className="text-sm text-muted">
              I testi riscritti arrivano poco dopo la lettura. Se approvi prima, l’evento nasce
              senza descrizione e la scrivi dalla scheda.
            </p>
          ) : (
            <p className="text-sm text-muted">
              Non serve riscrivere: l’evento esiste già e tiene i suoi testi.
            </p>
          )}
        </Card>
      </div>

      <h2 className="mt-8 text-label font-medium uppercase tracking-[0.06em]">
        Eventi simili ({c.matches.length})
      </h2>
      {c.matches.length === 0 && (
        <p className="mt-2 text-sm text-muted">
          Nessun evento nello stesso comune con date vicine.
        </p>
      )}
      <div className="mt-2 grid gap-3 lg:grid-cols-3">
        {c.matches.map((m) => {
          const ev = events.find((e) => e.id === m.id);
          const isBest = c.decision?.matchEventId === m.id || c.decision?.matchCandidateId === m.id;
          return (
            <section
              key={m.id}
              className={`flex flex-col gap-2 border p-3 text-sm ${isBest ? 'border-fg' : 'border-surface-strong'}`}
            >
              <div className="flex items-start justify-between gap-2">
                {m.kind === 'event' ? (
                  <Link to={`/events/${m.id}`} className="font-medium hover:underline">
                    {m.title}
                  </Link>
                ) : (
                  <Link to={`/review/${m.id}`} className="font-medium hover:underline">
                    {m.title} <span className="text-xs text-muted">(candidato)</span>
                  </Link>
                )}
                {ev && ev.status !== 'draft' && (
                  <a
                    href={`${config.siteUrl}/events/${ev.slug}`}
                    target="_blank"
                    rel="noopener"
                    className={btnGhost}
                    title="Apri sul sito"
                  >
                    <ExternalLink className="size-4" aria-hidden />
                  </a>
                )}
              </div>
              <div className="text-muted">
                {formatRange(m.startDate, m.endDate)}
                {ev && ` · ${categoryLabel(ev.category)} · ${ev.status}`}
              </div>
              <div className="text-xs text-muted">
                somiglianza {m.score.toFixed(2).replace('.', ',')} (titolo{' '}
                {m.titleScore.toFixed(2).replace('.', ',')}, date{' '}
                {m.dateScore.toFixed(2).replace('.', ',')})
              </div>
              {ev?.shortDesc && <p>{ev.shortDesc}</p>}
            </section>
          );
        })}
      </div>
    </main>
  );
}
